import React, { FC, useState, ChangeEvent } from 'react';
import { Order } from 'models/order';

import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { cancelOrderTemplate } from 'apollo/templates/order';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

type CancelByDentistDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const CancelByDentistDialog: FC<CancelByDentistDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: CancelByDentistDialogProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [message, setMessage] = useState<string>('');
    const { showBoundary } = useDenthubErrorBoundary();
    const handleMessageBox = (event: ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };

    const [changeStatus] = useMutation(cancelOrderTemplate, {
        onCompleted: ({ cancelOrder }) => {
            onClose();
            onUpdate?.(cancelOrder);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
                cancelMessage: message,
            },
        });
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:CANCEL_THE_ORDER')}
            actions={
                <Button
                    disabled={!message}
                    variant="contained"
                    onClick={handleChangeStatus}
                    data-test="cancel-order-dialog.cancel-btn"
                >
                    {translate('orders:CANCEL_THE_ORDER')}
                </Button>
            }
        >
            <Box mb={1}>
                <Typography variant="h4" mb={2}>
                    {translate('orders:WHY_ARE_YOU_CANCELLING_THE_ORDER')}
                </Typography>
                <TextField
                    fullWidth
                    minRows={6}
                    multiline
                    placeholder={translate('orders:ADD_AN_EXPLANATION_FOR_THE_LAB')}
                    value={message}
                    onChange={handleMessageBox}
                    data-test="cancel-order-dialog.message-input"
                />
            </Box>
            <Box>
                <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:WHEN_YOU_CANCEL_THE_ORDER_ALL_INFORMATION_WILL_BE_DELETED')}
                </Alert>
            </Box>
        </DHDialog>
    );
};
export default CancelByDentistDialog;
